const SCOPE = '.product__info-container';
const BATCH_MS = 50;

class ProductStickyBar extends HTMLElement {
  connectedCallback() {
    this.selector = this.dataset.target || '.product-form__submit';
    this.trigger = this.querySelector('[data-sticky-buy]');

    this.trigger?.addEventListener('click', (event) => {
      event.preventDefault();
      this.buyNow();
    });

    this.querySelector('[data-sticky-add]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.buyNow('[data-add-to-cart-only]');
    });

    // An IntersectionObserver watches the real buy button directly. A scroll
    // listener has to fire, has to be throttled, and re-measures on every
    // frame; this reports only when the button actually enters or leaves the
    // viewport, and keeps working inside any scroll container.
    this.observer = new IntersectionObserver(
      ([entry]) => this.toggle(!entry.isIntersecting),
      { rootMargin: '0px 0px -8px 0px', threshold: 0 }
    );

    this.watch();

    // Dawn swaps the buy button node out when the variant changes, so re-bind
    // whenever the section re-renders rather than holding a stale reference.
    // Scoped to the buy box, which holds both the button and the price: a
    // re-render of this page emits thousands of mutation records, and running
    // four selectors over the whole document for each batch is work for
    // nothing.
    this.rebind = new MutationObserver(() => this.schedule());
    this.rebind.observe(document.querySelector(SCOPE) || document.body, {
      childList: true,
      subtree: true,
      // The personalisation app adds option prices (a heart, +₹30) by
      // rewriting the price's text in place, which is a text change rather
      // than a node swap; without this the bar kept showing the base price.
      characterData: true,
    });

    this.refresh();
  }

  /**
   * Coalesces a burst of mutation records into one pass. A short timer rather
   * than requestAnimationFrame: animation frames are paused while the tab is
   * hidden, and the price must already be right when the shopper returns.
   */
  schedule() {
    if (this.queued) return;
    this.queued = true;
    setTimeout(() => {
      this.queued = false;
      this.refresh();
    }, BATCH_MS);
  }

  refresh() {
    const current = document.querySelector(this.selector);
    if (current && current !== this.watched) this.watch();
    this.syncPrice();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.rebind?.disconnect();
  }

  watch() {
    const atc = document.querySelector(this.selector);
    if (!atc) {
      this.toggle(false);
      return;
    }
    if (this.watched) this.observer.unobserve(this.watched);
    this.watched = atc;
    this.observer.observe(atc);
  }

  /**
   * The bar is its own section, so Dawn's variant re-render never touches it
   * and its Liquid-rendered price goes stale the moment someone picks the lit
   * base. Mirroring the buy box keeps one source of truth, and a stale price
   * on the button someone is about to press is worse than no price at all.
   */
  syncPrice() {
    const source = document.querySelector(SCOPE + ' .price');
    const now = this.querySelector('[data-sticky-price-now]');
    if (!source || !now) return;

    const onSale = source.classList.contains('price--on-sale');
    // A whole-rupee price loses its ".00" here: the bar is the narrowest row
    // on the page and the zeros were pushing the discount onto a second line.
    const read = (sel) => {
      const el = source.querySelector(sel);
      return el ? el.textContent.trim().replace(/\.00\b/, '') : '';
    };

    const price = onSale
      ? read('.price__sale .price-item--sale')
      : read('.price__regular .price-item--regular');
    if (!price) return;

    const compare = onSale ? read('.price__sale s.price-item--regular') : '';
    // The badge on the page carries a celebratory emoji; the bar has room for
    // the number and nothing else.
    const off = onSale ? read('.price__badge-sale').replace(/[^\x20-\x7E]/g, '').trim() : '';

    const next = [price, compare, off].join('|');
    if (next === this.lastPrice) return; // The observer fires on every render.
    this.lastPrice = next;

    now.textContent = price;
    this.setPart('[data-sticky-price-was]', compare);
    this.setPart('[data-sticky-price-off]', off);
  }

  setPart(selector, value) {
    const el = this.querySelector(selector);
    if (!el) return;
    el.textContent = value;
    el.hidden = !value;
  }

  toggle(visible) {
    this.classList.toggle('product-sticky-bar--visible', visible);
    // Lets the chat widget move clear of the bar.
    document.body.classList.toggle('sticky-buy-visible', visible);
  }

  // First required personalisation field the customer has not filled in yet.
  // Globo marks required options with a `label.required` inside `.gpo-element`.
  firstMissingField() {
    const groups = [...document.querySelectorAll('.gpo-element')];
    for (const group of groups) {
      if (!group.querySelector('label.required')) continue;
      const field = group.querySelector('input[type="text"], textarea, select');
      if (field && !String(field.value).trim()) return field;
    }
    return null;
  }

  // `target` is the in-page button to press: buy now by default, or the
  // add-to-cart-only button from the bar's cart button.
  buyNow(target = this.selector) {
    const missing = this.firstMissingField();

    // Complete: hand straight off to the real button so the options app
    // and Dawn's add-to-cart both run exactly as they normally would.
    if (!missing) {
      document.querySelector(target)?.click();
      return;
    }

    // Incomplete: the validation errors render up in the form, where someone
    // at the bottom of the page would never see them. Take them there.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: missing.getBoundingClientRect().top + window.scrollY - 140,
      behavior: reduced ? 'auto' : 'smooth',
    });
    window.setTimeout(() => missing.focus({ preventScroll: true }), reduced ? 0 : 450);
  }

}

if (!customElements.get('product-sticky-bar')) {
  customElements.define('product-sticky-bar', ProductStickyBar);
}
